import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import {
  Plus,
  Search,
  Printer,
  Edit,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { voucherService } from '@/services/voucherService'
import { VoucherListItem, VoucherType } from '@/api/types'

export const GeneralEntriesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [vouchers, setVouchers] = useState<VoucherListItem[]>([])
  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedVoucherType] = useState<string>('all')

  const branchId = user?.selectedBranch?.branchId

  // Set default dates (last month)
  useEffect(() => {
    const today = new Date()
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    
    setFromDate(lastMonth.toISOString().split('T')[0])
    setToDate(firstDay.toISOString().split('T')[0])
  }, [])

  // Load voucher types
  useEffect(() => {
    const loadVoucherTypes = async () => {
      try {
        const types = await voucherService.getVoucherTypes()
        setVoucherTypes(types)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVoucherTypes()
  }, [])

  // Load vouchers
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadVouchers = async () => {
      try {
        setLoading(true)
        const response = await voucherService.getAllVouchers(branchId, pageNumber, pageSize)
        const list = response.data || []
        setVouchers(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('generalEntries.failedToLoadGeneralEntries'))
      } finally {
        setLoading(false)
      }
    }

    loadVouchers()
  }, [branchId, pageNumber, pageSize])

  const filteredVouchers = useMemo(() => {
    return vouchers.filter((voucher) => {
      if (selectedVoucherType !== 'all' && voucher.voucherType !== selectedVoucherType) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (voucher.voucherCode || '').toLowerCase().includes(query) ||
        (voucher.refNo || '').toLowerCase().includes(query)
      )
    })
  }, [vouchers, selectedVoucherType, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleAddNew = () => {
    navigate('/general-entries/new')
  }

  const handleEdit = (voucher: VoucherListItem) => {
    const voucherId = voucher.voucherId ?? voucher.id
    if (!voucherId) {
      toast.error(t('generalEntries.invalidVoucherId'))
      return
    }
    navigate(`/general-entries/${voucherId}/edit`)
  }

  const handlePrint = (_voucher: VoucherListItem) => {
      toast.success(t('generalEntries.printFunctionalityComingSoon'))
  }


  const handleSearch = () => {
    // Search is handled by filteredVouchers
    setPageNumber(1)
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  // Get voucher type name by ID
  const getVoucherTypeName = (voucherTypeId: string | number): string => {
    if (!voucherTypeId) return 'N/A'
    const typeId = typeof voucherTypeId === 'string' ? parseInt(voucherTypeId) : voucherTypeId
    const type = voucherTypes.find(t => t.id === typeId)
    return type ? (type.name || type.nameEn || type.nameAr || '') : String(voucherTypeId)
  }

  // Get unique voucher types for filter - removed unused
  // const uniqueVoucherTypes = useMemo(() => {
  //   const types = new Set<string>()
  //   vouchers.forEach(v => {
  //     if (v.voucherType) {
  //       types.add(v.voucherType)
  //     }
  //   })
  //   return Array.from(types)
  // }, [vouchers])

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('generalEntries.title')}
          description={t('generalEntries.manageGeneralEntries')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('generalEntries.branchNotSelected')}
              description={t('generalEntries.selectBranchToViewGeneralEntries')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('generalEntries.title')}
        description={t('generalEntries.manageGeneralEntries')}
        action={{
          label: t('generalEntries.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>{t('generalEntries.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('generalEntries.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2 sm:col-span-2 md:col-span-1">
              <Button onClick={handleSearch} className="flex-1 md:flex-initial">
                <Search className="h-4 w-4 mr-2" />
                {t('generalEntries.search')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder={t('generalEntries.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('generalEntries.loadingGeneralEntries')}</p>
            </div>
          ) : filteredVouchers.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedVoucherType !== 'all' ? t('generalEntries.noGeneralEntriesFound') : t('generalEntries.noGeneralEntriesYet')}
                description={
                  searchQuery || selectedVoucherType !== 'all'
                    ? t('generalEntries.tryAdjustingSearch')
                    : t('generalEntries.getStarted')
                }
                action={
                  !searchQuery && selectedVoucherType === 'all'
                    ? {
                        label: t('generalEntries.addNewEntry'),
                        onClick: handleAddNew,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('generalEntries.id')}</TableHead>
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('generalEntries.generalEntryCode')}</TableHead>
                      <TableHead className={`font-bold text-primary hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('generalEntries.generalEntryType')}</TableHead>
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('generalEntries.generalEntryDate')}</TableHead>
                      <TableHead className="font-bold text-primary text-right px-2 sm:px-4">{t('generalEntries.totalDebit')}</TableHead>
                      <TableHead className="font-bold text-primary text-right px-2 sm:px-4">{t('generalEntries.totalCredit')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('generalEntries.print')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('generalEntries.edit')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVouchers.map((voucher) => {
                      const voucherId = voucher.voucherId ?? voucher.id
                      const isPosted = voucher.isPosted === true || voucher.isPosted === 1 || voucher.isPosted === 'Posted'
                      return (
                        <TableRow key={voucherId} className="border-b hover:bg-primary/5 transition-colors">
                          <TableCell className={`font-medium px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{voucherId}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{voucher.voucherCode || `GE-${voucherId}`}</TableCell>
                          <TableCell className={`hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{getVoucherTypeName(voucher.voucherType)}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(voucher.voucherEntryDate)}</TableCell>
                          <TableCell className="text-right px-2 sm:px-4">{formatAmount(voucher.totalDebit)}</TableCell>
                          <TableCell className="text-right px-2 sm:px-4">{formatAmount(voucher.totalCredit)}</TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button variant="ghost" size="icon" onClick={() => handlePrint(voucher)}>
                              <Printer className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(voucher)}
                              title={isPosted ? t('generalEntries.cannotEditPostedEntry') : t('generalEntries.editEntry')}
                              disabled={isPosted}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('generalEntries.showing')} {(pageNumber - 1) * pageSize + 1} {t('generalEntries.to')}{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} {t('generalEntries.of')} {totalCount} {t('generalEntries.entries')}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('generalEntries.pageSize')}</span>
                    <select
                      className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={String(pageSize)}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('generalEntries.previous')}</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap">
                    {t('generalEntries.page')} {pageNumber} {t('generalEntries.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    <span className="hidden sm:inline">{t('generalEntries.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

